import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertTitle,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  InputLabel,
  MenuItem,
  TextField
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import { get, isNil } from 'lodash';

import { ModalActions } from '@/RestoreComponent/ModalActions';
import { CustomerSchema } from '@/schemas';
import { LoadingButton } from '@mui/lab';
import { DateInput } from '@/components/DateInput';
import { formDataInstance } from '@/common/axios';
import CONFIG from '@/config';
import { getRestHeaders } from '@/common/utils/api';
import { PhoneInput } from '@/components/PhoneInput';
import { CustomerDetails } from '@/common/Selectors/CustomersSearch';

const useStyles = makeStyles((theme: any) => {
  return {
    heading: {
      paddingBottom: 12,
      height: 98
    },
    errorHeading: {
      borderBottomRightRadius: 0,
      borderBottomLeftRadius: 0,
      borderRightColor: 'transparent',
      borderTopColor: 'transparent',
      borderLeftColor: 'transparent'
    },
    label: {
      fontWeight: 600
    },
    saveErrorMessage: {
      fontWeight: 700,
      color: theme.palette.error.main
    }
  };
});

export const profileNeedsMissingInfo = (customerInfo: CustomerDetails) => {
  if (isNil(customerInfo)) {
    return true;
  } else {
    const result = [
      'firstName',
      'lastName',
      'dob',
      'email',
      'mobilePhone',
      'gender'
    ].filter(
      i =>
        isNil(customerInfo[i]) ||
        isNil(customerInfo[i]) ||
        customerInfo[i] === ''
    );
    return result.length !== 0;
  }
};

export interface Customer {
  id: number;
  first_name: string;
  last_name: string;
  gender: string;
  customerId: number;
  mobilePhone: number;
}

// Build the PATCH /staff/users payload for this modal. This form only edits the
// missing-info fields below, so we send ONLY those (plus customerId) instead of
// spreading the whole fetched customer record. Spreading re-sent fields the
// modal doesn't manage — notably referralSourceId/referredBy — which the
// backend then re-resolves and rejects (INVALID_REFERRAL_SOURCE /
// REFERRED_BY_REQUIRED_FOR_OTHER), 400-ing the update so gender never persisted.
//
// NOTE: this is a PARTIAL update. Fields not listed here (address, referral,
// home store, safety alert, …) are intentionally omitted and MUST be left
// untouched by the backend — StaffUpdateCustomer only applies params that are
// set, and TypeORM omits `undefined` from the generated SET clause. Do not
// change users-api's updateCustomer to coalesce undefined→null, and do not
// re-add a spread here: either would wipe those columns whenever staff edit
// gender through this modal.
export const buildClientUpdatePayload = (
  values: Record<string, any>,
  customerInfo: Customer
) => ({
  customerId: customerInfo.customerId || customerInfo.id,
  firstName: values.firstName,
  lastName: values.lastName,
  email: values.email,
  mobilePhone: values.mobilePhone,
  dob: values.dob,
  gender: values.gender
});

// Map the backend `error_code` (returned as response.data.error_code on a
// DomainException) to a complete, actionable save-failure message shown INSIDE the
// modal — replacing the old generic red snackbar. Flat code→message map mirroring
// the existing maps in AddEditCustomer/index.tsx and ScheduleAppointmentModal.
// Only the codes this modal's whitelisted payload can actually trigger are listed;
// referral / middle-name / emergency-contact codes are unreachable here.
const GENERIC_SAVE_ERROR =
  'The client update couldn’t be saved. Please refresh and try again.';

// Field-validation codes (from validateCustomerUpdateInfo) — name the offending
// field per the grooming decision's "...invalid information in the <field> field".
const fieldSaveErrorMessages: Record<string, string> = {
  INVALID_FIRST_NAME:
    'There is invalid information in the first name field. Please correct it and try again.',
  INVALID_LAST_NAME:
    'There is invalid information in the last name field. Please correct it and try again.',
  INVALID_EMAIL:
    'There is invalid information in the email field. Please correct it and try again.',
  INVALID_MOBILE_PHONE:
    'There is invalid information in the mobile phone field. Please correct it and try again.',
  INVALID_DOB:
    'There is invalid information in the date of birth field. Please correct it and try again.',
  INVALID_GENDER:
    'There is invalid information in the gender field. Please correct it and try again.'
};

// Non-field codes get their own wording — there is no form field to point at.
const saveErrorMessages: Record<string, string> = {
  ...fieldSaveErrorMessages,
  // Conflict — the reported real-world case: the email already belongs to
  // another client row, so the PATCH throws USER_EXISTS (not field validation).
  USER_EXISTS:
    'This email address is already used by another client. Verify the email or search for the existing client before saving.',
  // System/identity — nothing the user can fix in a field; fall back to generic.
  INVALID_CUSTOMER: GENERIC_SAVE_ERROR,
  FAILED_UPDATING: GENERIC_SAVE_ERROR
};

// Resolve a save-failure error_code to its message, falling back to the generic
// message for unknown/unmapped codes. Exported for unit testing.
export const resolveSaveErrorMessage = (errorCode?: string): string =>
  (errorCode && saveErrorMessages[errorCode]) || GENERIC_SAVE_ERROR;

// Client-side (Yup) validation maps a Formik field name to the SAME "...invalid
// information in the <field> field" message the backend would return for that
// field — so a frontend-caught error reads identically and points the user at
// the field, not just a red outline. Keyed by the field names this modal edits.
const fieldValidationMessages: Record<string, string> = {
  firstName: fieldSaveErrorMessages.INVALID_FIRST_NAME,
  lastName: fieldSaveErrorMessages.INVALID_LAST_NAME,
  email: fieldSaveErrorMessages.INVALID_EMAIL,
  mobilePhone: fieldSaveErrorMessages.INVALID_MOBILE_PHONE,
  dob: fieldSaveErrorMessages.INVALID_DOB,
  gender: fieldSaveErrorMessages.INVALID_GENDER
};

// Turn a Formik errors object into the list of field messages to show in-modal.
// Unlike the backend (which throws on the first bad field, one code), client-side
// validation can surface several at once — so this returns 1..n messages.
// Exported for unit testing.
export const resolveFieldValidationMessages = (
  errors: Record<string, unknown>
): string[] =>
  Object.keys(errors)
    .map(field => fieldValidationMessages[field])
    .filter(Boolean);

type EditClientProfileProps = {
  open: boolean;
  handleClose: (updated: boolean) => void;
  customerInfo: Customer;
  errorText: string;
  titleText: string;
};

export const EditClientProfile: React.FC<EditClientProfileProps> = ({
  open,
  handleClose,
  customerInfo,
  errorText,
  titleText
}) => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();
  // Save-failure / validation messages rendered in-modal (was a red snackbar that
  // also closed the modal). A list so client-side validation can show several
  // fields at once; a backend rejection contributes exactly one. Local to this
  // modal so it does not overwrite the parent's static `errorText` prop (which
  // explains why the modal opened).
  const [saveErrors, setSaveErrors] = useState<string[]>([]);

  // Clear stale messages whenever the modal (re)opens. Today's callers all
  // conditionally mount this component, so state already resets on reopen — this
  // is future-proofing in case a caller ever keeps it mounted (e.g. Dialog
  // keepMounted) so prior errors don't linger on the next open.
  useEffect(() => {
    if (open) {
      setSaveErrors([]);
    }
  }, [open]);

  // Send the whitelisted PATCH for this modal. Submitting state is managed by the
  // caller's helper so this works both as Formik's onSubmit and when invoked
  // directly from handleSave (which bypasses Formik's full-schema re-validation).
  const persistUpdate = async (
    values: Record<string, any>,
    { setSubmitting }: { setSubmitting: (isSubmitting: boolean) => void }
  ) => {
    // Clear any prior errors on each new attempt.
    setSaveErrors([]);
    setSubmitting(true);
    try {
      const variables = buildClientUpdatePayload(values, customerInfo);
      await formDataInstance.patch(
        `${CONFIG.USERS_API_URL}/staff/users/`,
        variables,
        { headers: getRestHeaders() }
      );
      enqueueSnackbar('Client updated successfully', { variant: 'success' });
      handleClose(true);
    } catch (e: any) {
      console.error(e);
      // Surface a specific, actionable message in-modal and keep the modal OPEN
      // so the user can correct the field / resolve the conflict. Do NOT close
      // the modal on a handled error.
      const errorCode = e?.response?.data?.error_code;
      setSaveErrors([resolveSaveErrorMessage(errorCode)]);
    } finally {
      setSubmitting(false);
    }
  };

  const formik = useFormik({
    initialValues: {
      ...customerInfo,
      dob: moment(get(customerInfo, 'dob')).utc().format('YYYY-MM-DD'),
      gender: customerInfo?.gender ? customerInfo.gender : null
    },
    validationSchema: CustomerSchema,
    enableReinitialize: true,
    // Kept so any future form submit still routes through the whitelisted
    // payload. It is not the primary path today — handleSave calls persistUpdate
    // directly so it can gate on the visible fields only; visible-field
    // validation gating lives there, not in Formik's full-schema submit.
    onSubmit: persistUpdate
  });

  const { getFieldProps } = formik;

  useEffect(() => {
    formik.validateForm();
  }, []);

  const handleSave = async () => {
    // Run client-side validation up front so we can surface the same field-level
    // messages the backend would — rather than only outlining the field in red.
    const errors = await formik.validateForm();
    // Only gate on errors for the fields THIS modal renders. The shared
    // CustomerSchema also validates fields not shown here (home/work/emergency
    // phone, etc.), whose stored values may not satisfy the strict rules; those
    // are not in fieldValidationMessages, so they are ignored. resolveField...
    // therefore returns messages for the visible fields only.
    const fieldMessages = resolveFieldValidationMessages(errors);
    if (fieldMessages.length > 0) {
      setSaveErrors(fieldMessages);
      // Touch the invalid fields so their red outline shows alongside the message.
      formik.setTouched(
        Object.keys(errors).reduce(
          (touched, field) => ({ ...touched, [field]: true }),
          {}
        )
      );
      return;
    }
    // Submit directly rather than formik.submitForm(), which would re-validate
    // the FULL schema and silently block on those unmanaged hidden fields.
    await persistUpdate(formik.values, { setSubmitting: formik.setSubmitting });
  };

  const title = titleText || 'Edit Client Profile';
  // Save/validation messages take precedence over the parent's static `errorText`.
  const hasSaveErrors = saveErrors.length > 0;
  const showErrorHeader = hasSaveErrors || Boolean(errorText);

  return (
    <Dialog open={open} onClose={() => handleClose(false)} maxWidth="sm">
      {showErrorHeader ? (
        <Alert severity="error" className={classes.errorHeading}>
          <AlertTitle>{title}</AlertTitle>
          {hasSaveErrors
            ? saveErrors.map(message => (
                <div key={message} className={classes.saveErrorMessage}>
                  {message}
                </div>
              ))
            : errorText}
        </Alert>
      ) : (
        <DialogTitle variant="h6" component="h2" className={classes.heading}>
          {title}
        </DialogTitle>
      )}
      <DialogContent>
        <Grid container mb={2} spacing={2}>
          <Grid item xs={6}>
            <InputLabel className={classes.label}>First Name</InputLabel>
            <TextField
              fullWidth
              name="firstName"
              placeholder="Legal Name"
              {...getFieldProps('firstName')}
              error={Boolean(get(formik, 'errors.firstName'))}
            />
          </Grid>
          <Grid item xs={6}>
            <InputLabel className={classes.label}>Last Name</InputLabel>
            <TextField
              fullWidth
              name="lastName"
              placeholder="Legal Name"
              {...getFieldProps('lastName')}
              error={Boolean(get(formik, 'errors.lastName'))}
            />
          </Grid>
        </Grid>
        <Grid container mb={2} spacing={2}>
          <Grid item xs={6}>
            <InputLabel className={classes.label}>Email</InputLabel>
            <TextField
              fullWidth
              name="email"
              placeholder="Email Address"
              {...getFieldProps('email')}
              error={Boolean(get(formik, 'errors.email'))}
            />
          </Grid>
          <Grid item xs={6}>
            <InputLabel className={classes.label}>Mobile Phone</InputLabel>
            <PhoneInput
              fullWidth
              name="mobilePhone"
              {...getFieldProps('mobilePhone')}
              hasError={Boolean(
                formik.touched.mobilePhone && formik.errors.mobilePhone
              )}
              helperText={
                formik.touched.mobilePhone && String(formik.errors.mobilePhone)
              }
              onChange={value => formik.setFieldValue('mobilePhone', value)}
            />
          </Grid>
        </Grid>
        <Grid container mb={1} spacing={2}>
          <Grid item xs={6}>
            <InputLabel className={classes.label}>Gender</InputLabel>
            <TextField
              select
              fullWidth
              name="gender"
              {...getFieldProps('gender')}
              error={Boolean(get(formik, 'errors.gender'))}
            >
              <MenuItem key="male" value="Male">
                Male
              </MenuItem>
              <MenuItem key="female" value="Female">
                Female
              </MenuItem>
              <MenuItem key="undisclosed" value="None">
                Undisclosed
              </MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={6}>
            <InputLabel className={classes.label}>Date of Birth</InputLabel>
            <DateInput
              fullWidth
              name="dob"
              InputLabelProps={{ shrink: true }}
              value={moment(get(formik, 'values.dob')).format('YYYY-MM-DD')}
              onChange={e => formik.setFieldValue('dob', e?.target?.value)}
              error={Boolean(get(formik, 'errors.dob'))}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <ModalActions>
        <Button variant="outlined" onClick={() => handleClose(false)}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          onClick={handleSave}
          loading={formik.isSubmitting}
          disabled={formik.isSubmitting}
        >
          Save and Continue
        </LoadingButton>
      </ModalActions>
    </Dialog>
  );
};
